import { useState, type ChangeEvent, type FocusEvent } from 'react';
import { InputAdornment } from '@mui/material';
import TextField from '../TextField/TextField';
import type { CurrencyInputProps } from './ICurrencyInput';
import { styles } from './styles';

const VALID_CURRENCY = /^\d*(\.\d{0,2})?$/;

const formatValue = (value: string): string => {
    if (value === '' || !VALID_CURRENCY.test(value)) return value;
    const num = parseFloat(value);
    return Number.isNaN(num) ? value : num.toFixed(2);
};

const CurrencyInput = ({
    value,
    onChange,
    onBlur,
    onFocus,
    symbol = '$',
    size,
    ...rest
}: CurrencyInputProps) => {
    const [focused, setFocused] = useState(false);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^\d.]/g, '');
        if (raw === '' || VALID_CURRENCY.test(raw)) {
            onChange(raw);
        }
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        onFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        const formatted = formatValue(value);
        if (formatted !== value) onChange(formatted);
        onBlur?.(e);
    };

    // While editing, show the raw value the user is typing; otherwise show it
    // formatted to two decimals so programmatically-set values (e.g. when
    // editing an existing rule) render formatted without needing a blur.
    const displayValue = focused ? value : formatValue(value);

    return (
        <TextField
            {...rest}
            size={size}
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start" sx={styles.adornment}>
                            {symbol}
                        </InputAdornment>
                    ),
                }
            }}
        />
    );
};

export default CurrencyInput;
